// Apps → Update Batches → one batch: the spreadsheet replacement. Every cell
// is read from the system and links to the screen where the action happens
// (Build tab, Play Console). The one action taken from here is kicking off an
// APK / AAB build for a row — the same call as the Build tab's button.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUpdateBatch, addBatchApps, removeBatchApp, dismissBatchBuild, archiveUpdateBatch, deleteUpdateBatch,
  type BatchApp, type BuildRef,
} from '@/api/update-batches';
import { Button } from '@/components/Button';
import { startBuild, downloadArtifact } from '@/api/app-builds';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { AppIcon, BundlePicker, describePick, type PickResult } from '@/components/update-batches/BundlePicker';
import { BatchStatusBadge, STAGES, StageBar, StageCaption, fmtUnix, pct, repoLabel, stageComplete, stageValue } from '@/components/update-batches/bits';
import { formatDate } from '@/lib/datetime';
import { shortBuildId } from '@/lib/build-id';

type Filter = 'all' | 'not_published' | 'blocked';

function hoursSince(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const h = Math.max(1, Math.round(ms / 3_600_000));
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

function buildLink(appId: string, buildId?: string): string {
  return `/apps-setup?tab=build&app=${encodeURIComponent(appId)}${buildId ? `&build=${encodeURIComponent(buildId)}` : ''}`;
}

/** The build's short id, so the row can be matched against the Build tab. */
function BuildId({ id }: { id: string }) {
  return <span className="font-mono text-xs text-content-subtle" title={`Build ${id}`}>#{shortBuildId(id)}</span>;
}
function playLink(a: BatchApp): string | null {
  return a.play_account_id != null ? `/play/${a.play_account_id}${a.play_app_id != null ? `?app=${a.play_app_id}` : ''}` : null;
}

export function UpdateBatchDetail() {
  const { id } = useParams();
  const batchId = Number(id);
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToastStore((s) => s.push);
  const [filter, setFilter] = useState<Filter>('all');
  const [showAdd, setShowAdd] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const batchQ = useQuery({
    queryKey: ['update-batch', batchId],
    queryFn: () => getUpdateBatch(batchId),
    enabled: Number.isFinite(batchId),
    refetchInterval: 60_000,
  });
  const batch = batchQ.data;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['update-batch', batchId] });
    qc.invalidateQueries({ queryKey: ['update-batches'] });
  };

  const add = useMutation({
    mutationFn: (appIds: string[]) => addBatchApps(batchId, appIds),
    onSuccess: (res) => {
      const busy = res.skipped.filter((s) => s.reason === 'busy');
      toast(`Added ${res.added.length} bundles${busy.length ? `, skipped ${busy.length} already in another active batch` : ''}`);
      invalidate();
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to add bundles'),
  });
  const remove = useMutation({
    mutationFn: (appId: string) => removeBatchApp(batchId, appId),
    onSuccess: invalidate,
    onError: (e: any) => toast(e?.message ?? 'Failed to remove bundle'),
  });
  const dismiss = useMutation({
    mutationFn: (buildId: string) => dismissBatchBuild(batchId, buildId),
    onSuccess: () => { toast('Build removed from the batch'); invalidate(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to remove the build'),
  });
  const archive = useMutation({
    mutationFn: () => archiveUpdateBatch(batchId),
    onSuccess: () => { toast('Batch archived — its bundles are free again'); invalidate(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to archive'),
  });
  const del = useMutation({
    mutationFn: () => deleteUpdateBatch(batchId),
    onSuccess: () => { toast('Batch deleted'); invalidate(); navigate('/apps-setup/update-batches'); },
    onError: (e: any) => toast(e?.message ?? 'Failed to delete'),
  });

  const onPick = (r: PickResult) => {
    setNotice(describePick(r));
    if (r.added.length > 0) add.mutate(r.added.map((c) => c.id));
  };

  const memberIds = useMemo(() => new Set((batch?.apps ?? []).map((a) => a.app_id)), [batch?.apps]);
  const counts = useMemo(() => {
    const apps = batch?.apps ?? [];
    return {
      all: apps.length,
      not_published: apps.filter((a) => a.published.state !== 'published').length,
      blocked: apps.filter((a) => a.qa.verdict === 'rejected').length,
    };
  }, [batch?.apps]);
  const rows = useMemo(() => {
    const apps = batch?.apps ?? [];
    if (filter === 'not_published') return apps.filter((a) => a.published.state !== 'published');
    if (filter === 'blocked') return apps.filter((a) => a.qa.verdict === 'rejected');
    return apps;
  }, [batch?.apps, filter]);

  if (batchQ.isLoading) return <div className="p-4 text-content-subtle">Loading…</div>;
  if (!batch) return <div className="p-4 text-danger">Batch not found.</div>;
  const p = batch.progress;
  const isActive = batch.status === 'active';

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div className="text-xs text-content-subtle">
        <Link to="/apps-setup/update-batches" className="hover:text-primary">Update Batches</Link> / {batch.name}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-content">{batch.name}</h1>
            <BatchStatusBadge status={batch.status} />
          </div>
          <div className="mt-1 text-sm text-content-subtle flex items-center gap-3 flex-wrap">
            <span className="inline-flex px-2 py-0.5 rounded bg-surface-muted text-xs text-content-muted" title={batch.repos.map((r) => r.name).join(', ')}>
              {repoLabel(batch.repos)}
            </span>
            <span>{batch.bundle_count} bundles</span>
            <span>Created by {batch.created_by_name || batch.created_by_email || '—'} · {fmtUnix(batch.created_at)}</span>
            {batch.closed_at && <span>{batch.status === 'completed' ? 'Completed' : 'Archived'} {fmtUnix(batch.closed_at)}</span>}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {isActive && (
              <Button variant="secondary" size="sm" loading={archive.isPending} onClick={() => {
                if (confirm('Archive this batch? Its bundles become free for a new batch; tracking stops here.')) archive.mutate();
              }}>Archive</Button>
            )}
            <Button variant="danger" size="sm" loading={del.isPending} onClick={() => {
              if (confirm(`Delete batch “${batch.name}”?`)) del.mutate();
            }}>Delete</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STAGES.map((s) => {
          const v = stageValue(p, s.key);
          return (
            <div key={s.key} className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-center justify-between text-xs uppercase tracking-wide text-content-subtle">
                <span>{s.label}</span>
                <span className={stageComplete(v) ? 'text-success' : 'text-primary'}>{pct(v.value, v.total)}%</span>
              </div>
              <div className="text-2xl font-semibold text-content mt-2">{v.value} / {v.total}</div>
              {v.failed !== undefined && <div className="text-xs mt-1"><StageCaption stage={v} /></div>}
              {!!v.review && <div className="text-xs mt-1 text-warning">⟳ {v.review} in review</div>}
              <div className="mt-3"><StageBar {...v} /></div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex gap-1 flex-wrap">
          {([['all', `All (${counts.all})`], ['not_published', `Not yet published (${counts.not_published})`], ['blocked', `Blocked on QA (${counts.blocked})`]] as [Filter, string][]).map(([f, label]) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm border ${filter === f ? 'border-primary bg-primary/10 text-content' : 'border-border text-content-muted hover:bg-surface-muted'}`}
            >{label}</button>
          ))}
        </div>
        {canEdit && isActive && (
          <Button variant="secondary" size="sm" onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Hide' : '+ Add bundles'}</Button>
        )}
      </div>

      {showAdd && isActive && (
        <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
          <BundlePicker onPick={onPick} excludeIds={memberIds} disabled={add.isPending} />
          {notice && <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-content">✓ {notice}</div>}
        </div>
      )}
      {!isActive && (
        <div className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-content-muted">
          This batch is {batch.status}; its bundles are free to join a new batch. Statuses below are still live.
        </div>
      )}

      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">App</th>
              <th className="text-left p-3 font-medium">APK Built</th>
              <th className="text-left p-3 font-medium">QA</th>
              <th className="text-left p-3 font-medium">Comment</th>
              <th className="text-left p-3 font-medium">AAB Built</th>
              <th className="text-left p-3 font-medium">Published</th>
              <th className="text-left p-3 font-medium">What's New Added</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-content-subtle">
                {batch.apps.length === 0 ? 'No bundles in this batch yet.' : 'Nothing matches this filter.'}
              </td></tr>
            )}
            {rows.map((a) => (
              <AppRow key={a.app_id} app={a} batchId={batchId} canRemove={canEdit && isActive} canBuild={canEdit && isActive} onBuilt={invalidate} onRemove={() => {
                if (confirm(`Remove ${a.name} from this batch?`)) remove.mutate(a.app_id);
              }} onDismissBuild={(type, ref) => {
                if (confirm(`Remove ${type.toUpperCase()} v${ref.version_name} (${ref.version_code}) from this batch?\nThe build stays in the Build tab history; the row goes back to “Not built”.`)) dismiss.mutate(ref.build_id);
              }} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Check({ ok }: { ok: boolean }) {
  return <span className={ok ? 'text-success' : 'text-content-subtle'}>{ok ? '✓' : '○'}</span>;
}

function AppRow({ app: a, batchId, canRemove, canBuild, onRemove, onBuilt, onDismissBuild }: {
  app: BatchApp; batchId: number; canRemove: boolean; canBuild: boolean; onRemove: () => void; onBuilt: () => void;
  onDismissBuild: (type: 'apk' | 'aab', ref: BuildRef) => void;
}) {
  const play = playLink(a);
  const toast = useToastStore((s) => s.push);
  const start = useMutation({
    mutationFn: (type: 'apk' | 'aab') => startBuild(a.app_id, type, { version_source: 'store', batch_id: batchId }),
    onSuccess: (b) => { toast(`${b.build_type.toUpperCase()} build v${b.version_name} (${b.version_code}) started for ${a.name}`); onBuilt(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to start the build'),
  });
  const notReady = !a.build_ready.ok;
  const buildBtn = (type: 'apk' | 'aab') => {
    const flying = a.in_flight[type];
    const pending = start.isPending && start.variables === type;
    const title = flying ? `An ${type.toUpperCase()} build is already running`
      : notReady ? `Cannot build yet — missing: ${a.build_ready.missing.join(', ')}. Set it up on the Build tab.`
      : `Start an ${type.toUpperCase()} build versioned as published + 1. It replaces this row's current ${type.toUpperCase()} once it succeeds.`;
    return (
      <button
        type="button"
        disabled={flying || notReady || start.isPending}
        onClick={() => start.mutate(type)}
        title={title}
        className={`px-2 py-0.5 rounded border text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed ${
          flying ? 'border-warning/40 text-warning'
          : notReady ? 'border-danger/50 text-danger'
          : 'border-border text-content-muted hover:border-primary hover:text-primary'
        }`}
      >
        {flying ? `${type.toUpperCase()} ⟳` : pending ? '…' : notReady ? `${type.toUpperCase()} ✕` : type.toUpperCase()}
      </button>
    );
  };
  const qaCls = a.qa.verdict === 'approved' ? 'bg-success-soft text-success'
    : a.qa.verdict === 'rejected' ? 'bg-danger-soft text-danger'
    : 'bg-primary/10 text-primary';
  const qaText = a.qa.verdict === 'approved' ? 'Approved' : a.qa.verdict === 'rejected' ? 'Rejected' : 'Not checked';

  return (
    <tr className={`border-t border-border ${a.done ? 'border-l-4 border-l-success bg-success-soft/40' : ''}`}>
      <td className="p-3 align-middle">
        <div className="flex items-center gap-3">
          <AppIcon url={a.icon_url} name={a.name} size={10} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-content truncate">{a.name}</span>
              {a.done && <span className="inline-flex px-1.5 py-0.5 rounded-full bg-success-soft text-success text-[11px] font-medium">✓ Done</span>}
            </div>
            <div className="text-xs font-mono text-content-subtle truncate">{a.bundle_id}</div>
            {/* A release often wants a fresh set: generate, localize or copy it in the listing drafts. */}
            {play && a.play_app_id != null && (
              <Link to={`${play}#screenshots`} className="text-xs text-primary hover:underline" title="Open the Play listing drafts at the phone screenshots">
                Screenshots →
              </Link>
            )}
          </div>
        </div>
      </td>
      <td className="p-3 align-middle">
        <Link to={buildLink(a.app_id, a.apk?.build_id)} className="block hover:underline" title="Open the Build tab">
          {a.apk ? <>
            <div className="text-content"><Check ok /> v{a.apk.version_name} ({a.apk.version_code})</div>
            <div className="text-xs text-content-subtle">{fmtUnix(a.apk.built_at)} · <BuildId id={a.apk.build_id} /></div>
          </> : <span className="text-content-subtle"><Check ok={false} /> Not built</span>}
        </Link>
        {a.apk && (
          <div className="mt-1 flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => downloadArtifact({ id: a.apk!.build_id })
                .catch((e: any) => toast(e?.message ?? 'Download failed'))}
              className="text-primary hover:underline"
              title="Download this APK"
            >
              ↓ Download APK
            </button>
            {canBuild && <DismissBuildButton type="apk" onClick={() => onDismissBuild('apk', a.apk!)} />}
          </div>
        )}
      </td>
      <td className="p-3 align-middle">
        <Link to={buildLink(a.app_id, a.apk?.build_id)} className="block" title="Verdicts are set on the Build tab">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${qaCls}`}>{qaText}</span>
          {a.apk && <div className="mt-1"><BuildId id={a.apk.build_id} /></div>}
        </Link>
      </td>
      <td className="p-3 align-middle max-w-[260px]">
        {a.qa.note
          ? <span className="text-content-muted text-xs whitespace-pre-wrap break-words">{a.qa.note}</span>
          : <span className="text-content-subtle text-xs italic">No comment yet</span>}
      </td>
      <td className="p-3 align-middle">
        <Link to={buildLink(a.app_id, a.aab?.build_id)} className="block hover:underline" title="Open the Build tab">
          {a.aab ? <>
            <div className="text-content"><Check ok /> v{a.aab.version_name} ({a.aab.version_code})</div>
            <div className="text-xs text-content-subtle">{fmtUnix(a.aab.built_at)} · <BuildId id={a.aab.build_id} /></div>
          </> : <span className="text-content-subtle"><Check ok={false} /> Not built</span>}
        </Link>
        {a.aab && canBuild && (
          <div className="mt-1 text-xs"><DismissBuildButton type="aab" onClick={() => onDismissBuild('aab', a.aab!)} /></div>
        )}
      </td>
      <td className="p-3 align-middle">
        <PublishedCell app={a} link={play} />
      </td>
      <td className="p-3 align-middle">
        {(() => {
          const body = a.whats_new.added ? <>
            <div className="text-content"><Check ok /> Added</div>
            <div className="text-xs text-content-subtle">{langsLabel(a.whats_new.langs)}</div>
          </> : <span className="text-content-subtle"><Check ok={false} /> Missing</span>;
          return play ? <Link to={play} className="block hover:underline" title="Open in Play Console">{body}</Link> : body;
        })()}
      </td>
      <td className="p-3 align-middle text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5">
          {canBuild && buildBtn('apk')}
          {canBuild && buildBtn('aab')}
          {canRemove && (
            <button onClick={onRemove} className="text-content-subtle hover:text-danger px-1" title="Remove from batch">×</button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Takes the pinned build off the batch (it stays in the Build tab history). */
function DismissBuildButton({ type, onClick }: { type: 'apk' | 'aab'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-content-subtle hover:text-danger"
      title={`Remove this ${type.toUpperCase()} from the batch — e.g. after a QA rejection, before building a new one. The build stays in the Build tab history.`}
    >
      × Remove
    </button>
  );
}

function PublishedCell({ app: a, link }: { app: BatchApp; link: string | null }) {
  const s = a.published;
  let body: React.ReactNode;
  if (s.state === 'published') {
    body = <>
      <div className="text-content"><Check ok /> Live on Play</div>
      <div className="text-xs text-content-subtle">{formatDate(s.since)}</div>
    </>;
  } else if (s.state === 'in_review') {
    body = <>
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-warning-soft text-warning">⟳ In review</span>
      <div className="text-xs text-content-subtle mt-1">Submitted {hoursSince(s.since)}</div>
    </>;
  } else if (s.state === 'no_play') {
    body = <span className="text-content-subtle" title="This app is not linked to a Play Console app">— No Play link</span>;
  } else {
    body = <span className="text-content-subtle"><Check ok={false} /> Not published</span>;
  }
  return link ? <Link to={link} className="block hover:underline" title="Open in Play Console">{body}</Link> : <div>{body}</div>;
}

function langsLabel(langs: string[]): string {
  const short = langs.map((l) => l.split('-')[0]!.toUpperCase());
  if (short.length === 0) return '';
  if (short.length === 1) return `${short[0]} only`;
  if (short.length <= 2) return short.join(', ');
  return `${short.slice(0, 2).join(', ')} +${short.length - 2}`;
}
